import React, { useState } from 'react';
import { X, User, Building2, CreditCard, Shield } from 'lucide-react';

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (employeeData: any) => void;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    employeeId: '',
    department: '',
    grade: '',
    email: '',
    phone: '',
    creditLimit: '',
    walletBalance: '',
    kycRequired: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const departments = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations'];
  const grades = ['Junior', 'Mid-Level', 'Senior', 'Manager', 'Director', 'Executive'];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors['name'] = 'Name is required';
    if (!formData.employeeId.trim()) newErrors['employeeId'] = 'Employee ID is required';
    if (!formData.department) newErrors['department'] = 'Department is required';
    if (!formData.grade) newErrors['grade'] = 'Grade is required';
    if (!formData.email.trim()) newErrors['email'] = 'Email is required';
    if (!formData.phone.trim()) newErrors['phone'] = 'Phone is required';
    if (!formData.creditLimit) newErrors['creditLimit'] = 'Credit limit is required';
    if (!formData.walletBalance) newErrors['walletBalance'] = 'Initial wallet balance is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors['email'] = 'Please enter a valid email address';
    }

    // Phone validation
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors['phone'] = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const employeeData = {
        ...formData,
        creditLimit: parseFloat(formData.creditLimit),
        walletBalance: parseFloat(formData.walletBalance),
        creditUsed: 0,
        status: 'pending' as const,
        kycStatus: 'pending' as const,
        joinDate: new Date().toISOString(),
        id: `emp_${Date.now()}`
      };
      onSave(employeeData);
      onClose();
      setFormData({
        name: '',
        employeeId: '',
        department: '',
        grade: '',
        email: '',
        phone: '',
        creditLimit: '',
        walletBalance: '',
        kycRequired: true
      });
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <User className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add New Employee</h2>
              <p className="text-sm text-gray-600">Create a new employee account</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-primary-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors['name'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="Enter full name"
                />
                {errors['name'] && <p className="mt-1 text-sm text-red-600">{errors['name']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee ID *
                </label>
                <input
                  type="text"
                  value={formData.employeeId}
                  onChange={(e) => handleInputChange('employeeId', e.target.value)}
                                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors['employeeId'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="e.g., EMP001"
                />
                {errors['employeeId'] && <p className="mt-1 text-sm text-red-600">{errors['employeeId']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["email"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="employee@company.com"
                />
                {errors["email"] && <p className="mt-1 text-sm text-red-600">{errors["email"]}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["phone"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+234 801 234 5678"
                />
                {errors["phone"] && <p className="mt-1 text-sm text-red-600">{errors["phone"]}</p>}
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-primary-600" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department *
                </label>
                <select
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["department"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                {errors["department"] && <p className="mt-1 text-sm text-red-600">{errors["department"]}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grade Level *
                </label>
                <select
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["grade"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
                {errors["grade"] && <p className="mt-1 text-sm text-red-600">{errors["grade"]}</p>}
              </div>
            </div>
          </div>

          {/* Financial Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
              Financial Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Limit (₦) *
                </label>
                <input
                  type="number"
                  value={formData.creditLimit}
                  onChange={(e) => handleInputChange('creditLimit', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["creditLimit"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="50000"
                  min="0"
                  step="1000"
                />
                {errors["creditLimit"] && <p className="mt-1 text-sm text-red-600">{errors["creditLimit"]}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Initial Wallet Balance (₦) *
                </label>
                <input
                  type="number"
                  value={formData.walletBalance}
                  onChange={(e) => handleInputChange('walletBalance', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["walletBalance"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="10000"
                  min="0"
                  step="1000"
                />
                {errors["walletBalance"] && <p className="mt-1 text-sm text-red-600">{errors["walletBalance"]}</p>}
              </div>
            </div>
          </div>

          {/* KYC Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-primary-600" />
              KYC & Security
            </h3>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="kycRequired"
                checked={formData.kycRequired}
                onChange={(e) => handleInputChange('kycRequired', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="kycRequired" className="ml-2 text-sm text-gray-700">
                Require KYC verification before account activation
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              <User className="h-4 w-4 mr-2" />
              Add Employee
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
